'use client';

// ============================================================
// portal-ux : micro-UX natives du portail salarié (mobile-first)
// ============================================================
// 1. haptic()        : retour haptique Android (no-op silencieux iOS,
//                      Safari n'expose pas navigator.vibrate)
// 2. usePullToRefresh: tirer vers le bas en haut de page pour rafraîchir
//                      (la PWA standalone n'a PAS le geste natif du
//                      navigateur, un salarié ne peut jamais rafraîchir)
// 3. useKeyboardInsets: quand le clavier iOS recouvre le champ focus,
//                      scrolle le champ au centre du viewport visible
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';

// ── 1. Haptique ────────────────────────────────────────────────

type HapticKind = 'tap' | 'success' | 'warning';

/** Micro-vibration Android. iOS ignore silencieusement (pas d'API web). */
export function haptic(kind: HapticKind = 'tap'): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  try {
    switch (kind) {
      case 'success':
        navigator.vibrate([12, 60, 18]);
        break;
      case 'warning':
        navigator.vibrate([30, 40, 30]);
        break;
      default:
        navigator.vibrate(10);
    }
  } catch {
    // Certains navigateurs jettent si l'utilisateur a désactivé la vibration.
  }
}

// ── 2. Pull-to-refresh ─────────────────────────────────────────

const PULL_THRESHOLD = 72; // px à tirer pour déclencher
const PULL_MAX = 110; // résistance : on ne suit plus le doigt au-delà

export interface PullToRefreshState {
  /** Distance de tirage amortie (px) pour translater l'indicateur. */
  pull: number;
  /** True pendant l'exécution du onRefresh (spinner). */
  refreshing: boolean;
  /** True quand le seuil est atteint (flèche → "relâcher pour actualiser"). */
  armed: boolean;
}

/**
 * Pull-to-refresh global (window scroll = body, conforme à la règle
 * "scroll natif"). Actif uniquement si la page est tout en haut ET que le
 * geste démarre hors d'une bottom sheet / d'un élément scrollable interne.
 */
export function usePullToRefresh(onRefresh: () => Promise<void>, enabled: boolean): PullToRefreshState {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const pulling = useRef(false);
  const refreshingRef = useRef(false);

  // Évite les re-subscribes à chaque render : onRefresh dans une ref.
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;

    const isInsideScrollable = (target: EventTarget | null): boolean => {
      let el = target instanceof Element ? target : null;
      while (el && el !== document.body) {
        // Sheets, panneaux et zones scrollables internes : pas de pull global.
        if (el.getAttribute('role') === 'dialog') return true;
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return true;
        el = el.parentElement;
      }
      return false;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (window.scrollY > 2) return;
      if (isInsideScrollable(e.target)) return;
      startY.current = e.touches[0].clientY;
      pulling.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current == null || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        if (!pulling.current) startY.current = null;
        return;
      }
      // Amortissement progressif type iOS (racine carrée).
      const damped = Math.min(PULL_MAX, Math.sqrt(dy) * 7);
      if (damped > 8) {
        pulling.current = true;
        setPull(damped);
        if (e.cancelable) e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      const distance = startY.current != null && pulling.current ? pull : 0;
      startY.current = null;
      pulling.current = false;
      if (distance >= PULL_THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPull(54); // l'indicateur reste visible pendant le refresh
        haptic('tap');
        void onRefreshRef.current().finally(() => {
          refreshingRef.current = false;
          setRefreshing(false);
          setPull(0);
        });
      } else {
        setPull(0);
      }
    };

    // passive:false sur touchmove : on doit pouvoir preventDefault pour
    // bloquer l'overscroll natif pendant le tirage.
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, pull]);

  return { pull, refreshing, armed: pull >= PULL_THRESHOLD };
}

// ── 3. Clavier mobile : garder le champ focus visible ─────────

/**
 * Sur iOS, le clavier réduit le visualViewport mais PAS le layout viewport :
 * un champ en bas de formulaire reste sous le clavier. On scrolle le champ
 * actif au centre de la zone réellement visible, après l'animation clavier.
 */
/**
 * UN CHAMP QUI OUVRE UNE LISTE DOIT REMONTER, PAS SE CENTRER.
 *
 * Le comportement normal centre le champ dans la zone visible. Quand une liste
 * de suggestions s'ouvre JUSTE EN DESSOUS, elle se retrouve alors sous le
 * clavier : on ne voit ni ce qu'on écrit, ni ce qui est proposé, et il faut
 * faire défiler à la main pour découvrir les choix (signalé le 09/09/2026).
 *
 * On amène donc le champ vers le HAUT de la zone réellement visible, en
 * laissant juste ce qu'il faut au-dessus pour garder son intitulé.
 *
 * Le déclencheur est l'appelant, pas le focus : la liste ne s'ouvre pas
 * toujours au focus, et remonter l'écran quand il n'y a rien à montrer serait
 * un mouvement gratuit et désagréable.
 */
export function remonterPourListe(element: HTMLElement | null, margeHaute = 84): void {
  if (!element) return;
  // Deux passes : la première dès maintenant pour que le mouvement paraisse
  // immédiat, la seconde une fois le clavier ouvert, car la zone visible n'a
  // pas la même hauteur avant et après.
  const amener = () => {
    const vv = window.visualViewport;
    const hautVisible = vv ? vv.offsetTop : 0;
    const rect = element.getBoundingClientRect();
    const delta = rect.top - hautVisible - margeHaute;
    if (Math.abs(delta) < 8) return;
    window.scrollBy({ top: delta, behavior: 'smooth' });
  };
  amener();
  // 350 ms : la durée d'ouverture du clavier, la même que celle déjà retenue
  // pour le maintien du champ visible.
  window.setTimeout(amener, 350);
}

export function useKeyboardInsets(enabled: boolean): void {
  const ensureVisible = useCallback(() => {
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)) return;
    if (!/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
    // Champs dans une bottom sheet : la sheet gère son propre scroll interne.
    if (el.closest('[role="dialog"]')) return;
    const vv = window.visualViewport;
    const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const rect = el.getBoundingClientRect();
    // 72px de marge : le champ ne doit pas coller le bord du clavier.
    if (rect.bottom > visibleBottom - 72 || rect.top < 0) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      // 350ms : laisse l'animation d'ouverture du clavier iOS se terminer.
      timer = setTimeout(ensureVisible, 350);
    };
    window.addEventListener('focusin', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('focusin', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
    };
  }, [enabled, ensureVisible]);
}
